import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Box from '../../Box/Box';
import Money from '../../Common/Money/Money';
import styles from './summary.module.css';

const Summary = ({ reservation }) => {
  const { t } = useTranslation();
  const currency = useSelector((state) => state.currency.selected);
  const voucherCode = useSelector((state) => state.reservation.voucherCode);
  //const transportation = useSelector((state) => state.reservation.transportation);
  const allCurrencies = useSelector((state) => state.currency.all);
  const customerCurrency = useSelector((state) => state.currency.selected);
  const paymentCurrency = allCurrencies.find((c) => c.shortForm === reservation.payment.currency);
  const showCurrencyConversion = customerCurrency.shortForm !== paymentCurrency.shortForm;

  const renderVoucherDiscount = () => {
    if (reservation.discounts?.length === 0) return;

    const discount = reservation.discounts.find((discount) => discount.discountType === 'DISCOUNT');

    if (!discount) return;

    return (
      <>
        <div className='row text-success mt-2'>
          <div className='col'>
            {t('coupon') + ' ' + voucherCode}
            {discount?.percent > 0 && ' (' + discount.percent + '%)'}:
          </div>
          <div className='col-auto fw-600'>
            <Money value={discount.amount} convert={false} lowDecimals decimal={false} />
          </div>
        </div>
        <div className='row mt-2'>
          <div className='col pr-0'>{t('subtotalForCommission')}</div>
          <div className='col-auto fw-600'>
            <Money
              value={reservation?.commissionableAmount}
              convert={false}
              lowDecimals
              decimal={false}
            />
          </div>
        </div>
      </>
    );
  };

  const renderSellerDiscount = () => {
    if (reservation.discounts?.length === 0) return;

    const discount = reservation.discounts.find(
      (discount) => discount.discountType === 'SELLER_DISCOUNT',
    );

    if (!discount) return;

    return (
      <div className='row text-success mt-2'>
        <div className='col'>
          {t('affiliateDiscountAbbr')} ({discount.percent}%):
        </div>
        <div className='col-auto fw-600'>
          <Money value={discount.amount} convert={false} lowDecimals decimal={false} />
        </div>
      </div>
    );
  };

  const renderChargeLabel = (charge) => {
    if (charge.chargeType === 'PERCENTAGE') {
      return <span className='font-smaller'>({charge.chargeTarget}%)</span>;
    }
    return (
      <span className='font-smaller'>
        ({currency.symbol}
        {charge.chargeTarget} {t('perPerson')})
      </span>
    );
  };

  return (
    <>
      <div className='section-title'>{t('summary')}</div>
      <Box>
        <div className='text-tertiary font-small fw-500'>
          {reservation.reservationType === 'AREA' ? (
            <div className='row'>
              <div className='col pr-0'>{t('productsInfo.numberOfPeople')}:</div>
              <div className='col-auto fw-400'>
                {t('upToGuests', { pax: reservation.paxs - reservation.kidsPaxs })}
              </div>
            </div>
          ) : reservation.reservationType === 'GROUP' ? (
            <div className='row'>
              <div className='col pr-0'>{t('productsInfo.numberOfPeople')}:</div>
              <div className='col-auto fw-400'>{reservation.paxs - reservation.kidsPaxs}</div>
            </div>
          ) : reservation?.reservationType === 'TICKET' &&
            reservation?.show?.purchaseTemplate === 'TRANSFER' ? (
            <div className='row'>
              <div className='col pr-0'>{t('productsInfo.numberOfPeople')}:</div>
              <div className='col-auto fw-400'>{reservation.paxs - reservation.kidsPaxs}</div>
            </div>
          ) : (
            <div className='row'>
              <div className='col pr-0'>{t('productsInfo.numberOfAdults')}:</div>
              <div className='col-auto fw-400'>{reservation.paxs - reservation.kidsPaxs}</div>
            </div>
          )}

          {reservation.kidsPaxs > 0 && (
            <div className='row mt-2'>
              <div className='col pr-0'>{t('productsInfo.numberOfChildren')}:</div>
              <div className='col-auto fw-400'>{reservation.kidsPaxs}</div>
            </div>
          )}

          <div className='row mt-2'>
            <div className='col pr-0'>{t('productsInfo.priceDetailsFor')}:</div>
            <div className='col-auto fw-400'>
              {reservation.items} {reservation.items > 1 ? t('items') : t('item')}
            </div>
          </div>

          {reservation.reservationType !== 'TRANSFER' &&
            reservation.products
              .filter((product) => product.reservationType === 'TRANSFER')
              .map((product) => (
                <div className='row mt-2' key={product.id}>
                  <div className='col pr-0'>{t('transportation')}</div>
                  <div className='col-auto'>
                    <Money
                      value={product.totalAmount}
                      convert={false}
                      lowDecimals
                      decimal={false}
                    />
                  </div>
                </div>
              ))}

          <div className='row mt-2'>
            <div className='col pr-0'>
              {reservation.promotion
                ? t('originalConsumptionSubtotal')
                : t('subtotalForCommission')}
              :
            </div>
            <div className='col-auto fw-600'>
              <Money
                value={reservation?.subTotalAmount}
                convert={false}
                lowDecimals
                decimal={false}
              />
            </div>
          </div>
          {renderVoucherDiscount()}
          {renderSellerDiscount()}

          {reservation.additionalChargesDetail &&
            reservation.additionalChargesDetail.length > 0 && (
              <>
                {reservation.additionalChargesDetail
                  .filter((charge) => charge.applyOn === null)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((charge, index) => (
                    <div key={`subtotal-${index}`} className='row mt-2 '>
                      <div className='col pr-0'>
                        {charge.name} {renderChargeLabel(charge)}:
                      </div>
                      <div className='col-auto fw-400'>
                        <Money
                          value={charge.chargeAmount}
                          convert={false}
                          lowDecimals
                          decimal={false}
                        />
                      </div>
                    </div>
                  ))}
              </>
            )}

          {reservation.additionalChargesDetail &&
            reservation.additionalChargesDetail.length > 0 && (
              <>
                {reservation.additionalChargesDetail
                  .filter((charge) => charge.applyOn === 'SUBTOTAL')
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((charge, index) => (
                    <div key={`subtotal-${index}`} className='row mt-2 '>
                      <div className='col pr-0'>
                        {charge.name} {renderChargeLabel(charge)}:
                      </div>
                      <div className='col-auto fw-400'>
                        <Money
                          value={charge.chargeAmount}
                          convert={false}
                          lowDecimals
                          decimal={false}
                        />
                      </div>
                    </div>
                  ))}
              </>
            )}

          {reservation.additionalChargesDetail &&
            reservation.additionalChargesDetail.length > 0 && (
              <>
                {reservation.additionalChargesDetail
                  .filter((charge) => charge.applyOn === 'TOTAL')
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((charge, index) => (
                    <div key={`total-${index}`} className='row mt-2'>
                      <div className='col pr-0'>
                        {charge.name} {renderChargeLabel(charge)}:
                      </div>
                      <div className='col-auto fw-400'>
                        <Money
                          value={charge.chargeAmount}
                          convert={false}
                          lowDecimals
                          decimal={false}
                        />
                      </div>
                    </div>
                  ))}
              </>
            )}

          {/* {reservation.additionalChargesDetail &&
            reservation.additionalChargesDetail.length > 0 && (
              <>
                {reservation.additionalChargesDetail
                  .filter((charge) => charge.applyOn === null)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((charge, index) => (
                    <div key={`null-${index}`} className='row mt-2'>
                      <div className='col pr-0'>
                        {charge.name}{' '}
                        <span className='font-smaller fw-300'>({charge.chargeTarget}%)</span>:
                      </div>
                      <div className='col-auto'>
                        <Money
                          value={charge.chargeAmount}
                          convert={false}
                          lowDecimals
                          decimal={false}
                        />
                      </div>
                    </div>
                  ))}
              </>
            )} */}

          <div className={`${styles.total}`}>
            <div className='row fw-700 font-normal text-white'>
              <div className='col pr-0'>
                {t('totalCustomer')} ({currency.shortForm}): {showCurrencyConversion && ' *'}
              </div>
              <div className='col-auto'>
                <Money
                  value={reservation?.totalAmount}
                  convert={false}
                  lowDecimals
                  decimal={false}
                />
              </div>
            </div>
          </div>
          {showCurrencyConversion && (
            <>
              <div className='row mt-2'>
                <div className='col'>{t('currencyCharge')}:</div>
                <div className='col-auto'>
                  <Money
                    currencyCode={paymentCurrency.shortForm}
                    value={reservation.payment.depositAmount}
                    convert={false}
                    lowDecimals
                    decimal={false}
                  />
                </div>
              </div>
              <div className='mt-3 font-tiny text-secondary'>
                {t('priceCurrencyExplanation', {
                  currency: currency.shortForm,
                  currencyPayment: reservation.payment.currency,
                })}
              </div>
            </>
          )}

          {reservation.remainingAmount > 0 ? (
            <>
              <div className='row mt-2'>
                <div className='col bold pr-0'>{t('productsInfo.depositGuarantee')}:</div>
                <div className='col-auto bold'>
                  <Money
                    value={reservation?.depositAmount}
                    currencyCode={reservation.payment.currency}
                    convert={false}
                    lowDecimals
                    decimal={false}
                  />
                </div>
              </div>
              <div className='row mt-2'>
                <div className='col bold pr-0'>{t('productsInfo.balanceToBePaid')}:</div>
                <div className='col-auto bold'>
                  <Money
                    value={reservation?.remainingAmount}
                    currencyCode={reservation.payment.currency}
                    convert={false}
                    lowDecimals
                    decimal={false}
                  />
                </div>
              </div>
            </>
          ) : null}

          {/* {transportation ? (
            <div className='mt-4'>
              <span className='fw-semibold text-primary font-tiny'>{t('transportation')}</span>
              <div className='row mt-2'>
                <div className='col-auto text-lighter-gray pr-0'>{t('pickupLocation')}:</div>
                <div className='col fw-600 text-right'>
                  {transportation.name} - {transportation.description}
                </div>
              </div>
              <div className='row mt-1'>
                <div className='col text-lighter-gray pr-0'>{t('pickupTime')}:</div>
                <div className='col-auto text-lighter-gray'>{transportation.time}</div>
              </div>
            </div>
          ) : null} */}
        </div>
      </Box>
    </>
  );
};

export default Summary;
